import { motion } from "framer-motion";
import { useT } from "../lib/i18n";

/**
 * Partner wall — minimalist white-out grid on dark canvas.
 *
 * Every logo renders as a uniform white silhouette by default (via the
 * CSS `brightness(0) invert(1)` filter) so the wall reads as a single,
 * cohesive composition independent of brand colour.
 *
 * On hover, the filter is removed and the original HD artwork (SVG when
 * available, PNG otherwise) is revealed in full colour, paired with a
 * subtle lift + scale for a tactile, premium reveal.
 *
 * Groups:
 *   • HERO    — flagship clients (larger)
 *   • REGULAR — additional clients
 *   • MEDIA   — outlets that PUBLISHED Romain's photos
 */

// HD vector sources where available (.svg) — PNG fallback otherwise.
// `mode: "boxed"` is applied to logos whose design relies on internal
// contrast (e.g. white text on a coloured box). For those we use a soft
// grayscale-dim instead of a full white-out so the internal contrast
// stays readable.
const HERO = [
  { id: 1,  src: "/partner-logos/logo_01.svg", alt: "France.tv" },
  { id: 4,  src: "/partner-logos/logo_04.svg", alt: "Canal+", mode: "boxed" },
  { id: 19, src: "/partner-logos/logo_19.png", alt: "EDF" },
  { id: 20, src: "/partner-logos/logo_20.svg", alt: "Decathlon" },
  { id: 10, src: "/partner-logos/logo_10.svg", alt: "Groupama" },
];

const REGULAR = [
  { id: 2,  src: "/partner-logos/logo_02.png", alt: "L'Équipe" },
  { id: 6,  src: "/partner-logos/logo_06.png", alt: "Région Auvergne-Rhône-Alpes" },
  { id: 9,  src: "/partner-logos/logo_09.png", alt: "Agence Nationale du Sport", mode: "boxed" },
  { id: 5,  src: "/partner-logos/logo_05.svg", alt: "International Canoe Federation" },
  { id: 16, src: "/partner-logos/logo_16.png", alt: "European Canoe Association" },
  { id: 8,  src: "/partner-logos/logo_08.png", alt: "Simond" },
  { id: 11, src: "/partner-logos/logo_11.png", alt: "Parc Aquasports Pau Béarn Pyrénées" },
  { id: 13, src: "/partner-logos/logo_13.png", alt: "Tout le monde contre le cancer", mode: "boxed" },
  { id: 3,  src: "/partner-logos/logo_03.png", alt: "Groupe Via", mode: "boxed" },
  { id: 12, src: "/partner-logos/logo_12.png", alt: "Via Immobilier" },
  { id: 15, src: "/partner-logos/logo_15.png", alt: "Éklore Éd." },
];

const MEDIA = [
  { id: 17, src: "/partner-logos/logo_17.png", alt: "BBC" },
  { id: 18, src: "/partner-logos/logo_18.png", alt: "RMC Sport", mode: "boxed" },
  { id: 7,  src: "/partner-logos/logo_07.png", alt: "Hugo Décrypte", mode: "boxed" },
];

const FlagFRA = () => (
  <svg viewBox="0 0 60 40" className="w-full h-full">
    <rect x="0"  y="0" width="20" height="40" fill="#0055A4" />
    <rect x="20" y="0" width="20" height="40" fill="#FFFFFF" />
    <rect x="40" y="0" width="20" height="40" fill="#EF4135" />
  </svg>
);
const FlagITA = () => (
  <svg viewBox="0 0 60 40" className="w-full h-full">
    <rect x="0"  y="0" width="20" height="40" fill="#008C45" />
    <rect x="20" y="0" width="20" height="40" fill="#F4F5F0" />
    <rect x="40" y="0" width="20" height="40" fill="#CD212A" />
  </svg>
);
const FlagGBR = () => (
  <svg viewBox="0 0 60 40" className="w-full h-full">
    <rect width="60" height="40" fill="#012169" />
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#FFFFFF" strokeWidth="8" />
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="4" />
    <path d="M30,0 V40 M0,20 H60" stroke="#FFFFFF" strokeWidth="10" />
    <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);
const FlagAUS = () => (
  <svg viewBox="0 0 60 40" className="w-full h-full">
    <rect width="60" height="40" fill="#012169" />
    <g>
      <rect width="30" height="20" fill="#012169" />
      <path d="M0,0 L30,20 M30,0 L0,20" stroke="#FFFFFF" strokeWidth="3" />
      <path d="M0,0 L30,20 M30,0 L0,20" stroke="#C8102E" strokeWidth="1.5" />
      <path d="M15,0 V20 M0,10 H30" stroke="#FFFFFF" strokeWidth="4" />
      <path d="M15,0 V20 M0,10 H30" stroke="#C8102E" strokeWidth="2.5" />
    </g>
    <circle cx="15" cy="30" r="3.2" fill="#FFFFFF" />
    <circle cx="46" cy="11" r="1.2" fill="#FFFFFF" />
    <circle cx="54" cy="22" r="1.4" fill="#FFFFFF" />
    <circle cx="42" cy="26" r="1.0" fill="#FFFFFF" />
    <circle cx="50" cy="32" r="1.3" fill="#FFFFFF" />
    <circle cx="48" cy="18" r="0.7" fill="#FFFFFF" />
  </svg>
);
const FlagBRA = () => (
  <svg viewBox="0 0 60 40" className="w-full h-full">
    <rect width="60" height="40" fill="#009C3B" />
    <polygon points="30,5 55,20 30,35 5,20" fill="#FFDF00" />
    <circle cx="30" cy="20" r="7.5" fill="#002776" />
    <path d="M22.5,18 Q30,15 37.5,18" stroke="#FFFFFF" strokeWidth="0.8" fill="none" />
  </svg>
);
const FlagCAN = () => (
  <svg viewBox="0 0 60 40" className="w-full h-full">
    <rect x="0"  y="0" width="15" height="40" fill="#FF0000" />
    <rect x="15" y="0" width="30" height="40" fill="#FFFFFF" />
    <rect x="45" y="0" width="15" height="40" fill="#FF0000" />
    <path d="M30 11 L31.6 15 L35.4 14 L33.4 17.6 L36 19 L33.4 19.8 L34 23.4 L31.4 21.4 L30 24 L28.6 21.4 L26 23.4 L26.6 19.8 L24 19 L26.6 17.6 L24.6 14 L28.4 15 Z" fill="#FF0000" />
  </svg>
);

const FEDERATIONS = [
  { code: "FRA", label: "France",        Flag: FlagFRA },
  { code: "GBR", label: "Great Britain", Flag: FlagGBR },
  { code: "AUS", label: "Australia",     Flag: FlagAUS },
  { code: "BRA", label: "Brésil",        Flag: FlagBRA },
  { code: "ITA", label: "Italie",        Flag: FlagITA },
  { code: "CAN", label: "Canada",        Flag: FlagCAN },
];

/**
 * Logo tile: white-out silhouette by default, full HD colour on hover.
 * The `partner-logo` CSS class drives the filter + opacity transitions
 * (see /app/frontend/src/App.css).
 */
const LogoCell = ({ item, idx, sizeCls }) => (
  <motion.div
    data-testid={`partner-logo-${item.id}`}
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.55, delay: (idx % 8) * 0.05, ease: [0.22, 1, 0.36, 1] }}
    className="group flex items-center justify-center px-2 py-1 sm:px-3 sm:py-3 md:px-4 md:py-4 w-[42%] sm:w-[28%] md:w-auto"
  >
    <img
      src={item.src}
      alt={item.alt}
      loading="lazy"
      decoding="async"
      className={`${sizeCls} w-auto max-w-full object-contain ${
        item.mode === "boxed" ? "partner-logo partner-logo-boxed" : "partner-logo"
      }`}
    />
  </motion.div>
);

export const PartnerLogos = () => {
  const t = useT();
  return (
    <section
      data-testid="partners-section"
      className="bg-ink text-bone relative py-20 md:py-28 border-y border-bone/8"
    >
      {/* Subtle warm radial accents */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(ellipse at 20% 0%, rgba(184,169,154,0.10) 0%, rgba(0,0,0,0) 55%), radial-gradient(ellipse at 80% 100%, rgba(184,169,154,0.06) 0%, rgba(0,0,0,0) 55%)",
        }}
      />

      <div className="container-x relative">
        {/* Editorial header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-10 bg-stone/50" />
            <div className="eyebrow text-stone">{t("partners.eyebrow")}</div>
            <span className="h-px w-10 bg-stone/50" />
          </div>
          <h2 className="font-display text-4xl md:text-6xl text-bone leading-[0.95]">
            {t("partners.headlineA")}{" "}
            <span className="italic text-stone">{t("partners.headlineB")}</span>.
          </h2>
          <p className="mt-5 text-[10px] tracking-[0.28em] uppercase text-bone/45">
            {t("partners.sub")}
          </p>
        </div>

        {/* HERO clients — centered flex wrap so any row aligns center, no orphaned right gap */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-10 md:gap-x-12 gap-y-8 md:gap-y-14 max-w-6xl mx-auto"
          data-testid="partners-hero-grid"
        >
          {HERO.map((p, i) => (
            <LogoCell key={p.id} item={p} idx={i} sizeCls="h-9 sm:h-12 md:h-16 lg:h-20" />
          ))}
        </div>

        {/* Divider */}
        <div className="max-w-3xl mx-auto my-14 md:my-16 flex items-center gap-4">
          <span className="h-px flex-1 bg-bone/10" />
          <span className="text-[10px] tracking-[0.28em] uppercase text-bone/35">
            & more
          </span>
          <span className="h-px flex-1 bg-bone/10" />
        </div>

        {/* REGULAR clients — same centered flex pattern; trailing rows collapse to center automatically */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-8 sm:gap-x-12 md:gap-x-14 gap-y-10 md:gap-y-12 max-w-5xl mx-auto"
          data-testid="partners-regular-grid"
        >
          {REGULAR.map((p, i) => (
            <LogoCell key={p.id} item={p} idx={i} sizeCls="h-7 sm:h-9 md:h-11 lg:h-12" />
          ))}
        </div>

        {/* MEDIA */}
        <div className="mt-20 md:mt-24 border-t border-bone/10 pt-12">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="eyebrow text-stone mb-2">{t("partners.press.eyebrow")}</div>
            <p className="text-sm md:text-base text-bone/55 italic font-light">
              {t("partners.press.intro")}
            </p>
          </div>
          <div
            className="grid grid-cols-3 gap-x-12 gap-y-10 items-center justify-items-center max-w-3xl mx-auto"
            data-testid="partners-media-grid"
          >
            {MEDIA.map((p, i) => (
              <LogoCell key={p.id} item={p} idx={i} sizeCls="h-10 md:h-12" />
            ))}
          </div>
        </div>

        {/* Federations */}
        <div className="mt-20 md:mt-24 border-t border-bone/10 pt-12">
          <div className="text-center eyebrow text-stone mb-10">
            {t("partners.federations")}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 md:gap-x-14 gap-y-10">
            {FEDERATIONS.map((f, i) => {
              const Flag = f.Flag;
              return (
                <motion.div
                  key={f.code}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`fed-${f.code.toLowerCase()}`}
                  className="group flex flex-col items-center text-center cursor-default"
                >
                  <div className="relative w-14 h-9 md:w-16 md:h-10 overflow-hidden rounded-[3px] ring-1 ring-bone/20 shadow-[0_3px_10px_-3px_rgba(0,0,0,0.4)] transition-all duration-300 group-hover:-translate-y-1.5 group-hover:shadow-[0_10px_22px_-6px_rgba(0,0,0,0.55)] group-hover:ring-bone/40">
                    <Flag />
                    <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/12 via-transparent to-black/15" />
                  </div>
                  <div className="font-display text-lg md:text-xl mt-2.5 leading-none tracking-tight text-bone">
                    {f.code}
                  </div>
                  <div className="text-[9px] tracking-[0.22em] uppercase text-bone/45 mt-1">
                    {f.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerLogos;
